import {PHASES} from "../../../constants/phases";
import {COMMON_BUILDING_TOKENS} from "../../../constants/tokens";

/**
 * ОДНОВРЕМЕННАЯ ФАЗА СНАБЖЕНИЯ СЕТЕВОЙ ИГРЫ.
 *
 * В сетевой партии фазу снабжения все игроки проводят разом, а не по очереди:
 * каждый раздает снабжение своим отрядам и завершает свою часть фазы
 * (COMPLETE_PLAYER_SUPPLY), а саму фазу закрывает сервер, когда закончили все
 * (или вышло общее время фазы). Итог снабжения — рост, потеря размера, гибель
 * отрядов — применяется ВСЕМ сразу при закрытии фазы, на одном и том же поле:
 * результат не зависит от того, кто быстрее нажал.
 *
 * Режим — явный флаг gameState.isParallelSupply из START_GAME, а не тип игры:
 * запись матча воспроизводится с типом REPLAY и обязана повторять правила
 * своей партии, а записи, сделанные до появления режима, — остаться
 * поочередными. Локальные игры, обучение и миссии флаг не получают.
 *
 * Пока фаза идет, currentPlayerIndex не двигается (как в расстановке): он
 * остается якорем для слепка хода (createPhaseActionGuard) — иначе завершение
 * одного игрока делало бы устаревшими все летящие действия остальных.
 * Кто уже закончил — phaseCompletedPlayers.
 */

/**
 * Идет ли сейчас одновременная фаза снабжения. Партия, закончившаяся на входе в
 * снабжение (итоги последнего раунда), фазу уже не играет
 */
export const checkIfParallelSupplyPhase = (gameState) => Boolean(gameState?.isParallelSupply)
  && gameState?.phase === PHASES.SUPPLY
  && !(gameState?.gameResults?.length > 0);

/** Закончил ли игрок текущую фазу */
export const checkIfPlayerCompletedPhase = (gameState, playerId) => Boolean(playerId)
  && (gameState?.phaseCompletedPlayers || []).includes(playerId);

/**
 * Игроки, еще не закончившие фазу, — в порядке хода (turnOrder), чтобы все
 * клиенты показывали одного и того же «ожидаемого» игрока
 */
export const getNotCompletedPlayers = (gameState) => {
  const players = gameState?.players || [];
  const completed = new Set(gameState?.phaseCompletedPlayers || []);
  const order = gameState?.turnOrder?.length ? gameState.turnOrder : players.map((player) => player.id);
  const ordered = order
    .map((playerId) => players.find((player) => player.id === playerId))
    .filter(Boolean);
  // Игрок, которого нет в turnOrder (не должно случаться), — в конце списка
  players.forEach((player) => {
    if (!ordered.includes(player)) {
      ordered.push(player);
    }
  });
  return ordered.filter((player) => !completed.has(player.id));
};

/** Первый в порядке хода игрок, не закончивший фазу, или null */
export const getFirstNotCompletedPlayer = (gameState) => getNotCompletedPlayers(gameState)[0] || null;

/** Закончили ли фазу все игроки партии */
export const checkIfAllPlayersCompletedPhase = (gameState) => {
  const players = gameState?.players || [];
  return players.length > 0 && getNotCompletedPlayers(gameState).length === 0;
};

/**
 * Игрок, за которого выполняется действие снабжения: явный payload.playerId,
 * если такой игрок есть в партии, иначе текущий игрок. Запасной вариант —
 * для записей матчей и сюжетных миссий, сделанных до появления playerId:
 * тогда снабжение раздавал только ходящий игрок
 */
export const resolveSupplyActorId = (gameState, payload) => {
  const players = gameState?.players || [];
  const playerId = payload?.playerId;
  if (playerId && players.some((player) => player.id === playerId)) {
    return playerId;
  }
  return players[gameState?.currentPlayerIndex]?.id || null;
};

/**
 * Чье это отложенное снабжение. Новые записи несут playerId; у старых владелец
 * выводится из снабжаемого отряда, а обмен снабжения по аграризму (записи без
 * отряда) — из владельца фермы
 */
export const getPendingSupplyOwnerId = (entry, gameState) => {
  if (!entry) {
    return null;
  }
  if (entry.playerId) {
    return entry.playerId;
  }
  if (entry.unitId) {
    const unit = (gameState?.units || []).find((candidate) => candidate.id === entry.unitId);
    if (unit) {
      return unit.ownerId;
    }
  }
  const farmHexKeys = entry.farmHexKeys || (entry.farmHexKey ? [entry.farmHexKey] : []);
  const citiesCache = gameState?.citiesCache || {};
  for (const player of (gameState?.players || [])) {
    const cities = citiesCache[player.color] || [];
    const ownsFarm = cities.some((city) => (city[COMMON_BUILDING_TOKENS.FARM] || []).some((farmHexKey) => farmHexKeys.includes(farmHexKey)));
    if (ownsFarm) {
      return player.id;
    }
  }
  return null;
};

/** Отложенное снабжение одного игрока */
export const filterPlayerPendingSupply = (pendingSupply, gameState, playerId) => (pendingSupply || [])
  .filter((entry) => getPendingSupplyOwnerId(entry, gameState) === playerId);

/**
 * Действия фазы снабжения, которые совершаются ЗА КОНКРЕТНОГО игрока
 * (payload.playerId). В одновременной фазе сервер проверяет, что отправитель
 * вправе действовать за этого игрока (сам игрок или админ, перехвативший бота),
 * а редьюсеры игнорируют их от игрока, уже закончившего фазу
 */
export const PARALLEL_SUPPLY_ACTOR_ACTION_TYPES = [
  'DISTRIBUTE_SUPPLY',
  'DISTRIBUTE_GIANTS_ADDITIONAL_SUPPLY',
  'GIANTS_CONVERT_SUPPLY',
  'GIANTS_SELECT_UNIT_FOR_ADDITIONAL_SUPPLY',
  'GIANTS_RESET_ADDITIONAL_SUPPLY_SELECTION',
  'GIANTS_TOGGLE_AUTO_ADDITIONAL_SUPPLY',
  'UNDO_PENDING_SUPPLY_ACTION',
  'UNDO_UNIT_PENDING_SUPPLY',
  'UNDO_FARM_PENDING_SUPPLY',
  'RESET_PENDING_SUPPLY',
  'RECRUIT_UNIT',
  'POLICY_FANATICISM_CONVERT_WIN_POINTS',
  'POLICY_AGRARIANISM_SWITCH_CONVERSION_ON',
  'POLICY_AGRARIANISM_SWITCH_CONVERSION_OFF',
  'POLICY_AGRARIANISM_SELECT_FARM_FOR_CONVERSION',
  'POLICY_AGRARIANISM_COMMIT_CONVERSION',
  'COMPLETE_PLAYER_SUPPLY',
];

/**
 * Действие снабжения за игрока, уже закончившего одновременную фазу: его часть
 * фазы закрыта, и поздняя раздача или отмена изменила бы уже принятый итог
 */
export const checkIfSupplyActorLocked = (gameState, actorId) => checkIfParallelSupplyPhase(gameState)
  && checkIfPlayerCompletedPhase(gameState, actorId);
